/**
 * Textes des volets du 2026-10-06 (français, langue de référence) : le volet social (#52, clé
 * `social`) et la galerie de gabarits low-code avec « Vérifier » (#54, clé `gallery`). Branchés
 * dans `fr.ts` ; `social.en.ts` porte exactement les mêmes clés (typage + `i18n.test.ts`).
 */
export const socialFr = {
  page: {
    title: 'Activité du projet',
    short: 'Activité',
    subtitle:
      'Ce qui s’est passé dans {{project}} et ses jobs, workflows, expériences et endpoints. Réagissez, commentez, suivez.',
  },
  summary: {
    contributions: 'Contributions',
    activeDays: 'Jours actifs',
    since: 'Depuis',
  },
  feed: {
    label: 'Fil d’activité',
    loadMore: 'Charger plus',
    platform: 'La plateforme',
    times_one: '×{{count}}',
    times_other: '×{{count}}',
    unavailable: 'Le fil d’activité n’est pas activé sur cette installation.',
    empty: {
      title: 'Aucune activité pour l’instant',
      description: 'Créer un job, lancer un run ou suivre une ressource l’inscrit ici.',
    },
  },
  verbs: {
    projectcreation: 'a créé le projet',
    projectupdate: 'a modifié le projet',
    projectdeletion: 'a supprimé le projet',
    jobcreation: 'a créé le job',
    jobupdate: 'a modifié le job',
    jobdeletion: 'a supprimé le job',
    jobrun: 'a lancé le job',
    workflowcreation: 'a créé le workflow',
    workflowupdate: 'a modifié le workflow',
    workflowdeletion: 'a supprimé le workflow',
    workflowrun: 'a lancé le workflow',
    experimentcreation: 'a créé l’expérience',
    experimentupdate: 'a modifié l’expérience',
    experimentdeletion: 'a supprimé l’expérience',
    endpointcreation: 'a créé l’endpoint',
    endpointupdate: 'a modifié l’endpoint',
    endpointdeletion: 'a supprimé l’endpoint',
    userfollow: 'suit',
    fallback: 'a agi sur {{type}}',
  },
  followVerbs: {
    follow: 'suit {{type}}',
    unfollow: 'ne suit plus {{type}}',
  },
  types: {
    project: 'le projet',
    job: 'le job',
    workflow: 'le workflow',
    experiment: 'l’expérience',
    endpoint: 'l’endpoint',
    catalog: 'le catalogue',
    database: 'la base',
    table: 'la table',
    user: 'l’utilisateur',
    resource: 'la ressource',
  },
  typeNames: {
    project: 'Projet',
    job: 'Job',
    workflow: 'Workflow',
    experiment: 'Expérience',
    endpoint: 'Endpoint',
    catalog: 'Catalogue',
    database: 'Base',
    table: 'Table',
    user: 'Utilisateur',
  },
  profile: {
    subtitle: 'Votre fil : vos gestes et l’activité de ce que vous suivez.',
    tabs: { feed: 'Mon fil', following: 'Abonnements' },
    followingHelp: 'Ce que vous suivez : son activité apparaît dans votre fil.',
    columns: { type: 'Type', name: 'Ressource' },
    unfollow: 'Ne plus suivre',
    unfollowed: 'Abonnement retiré.',
    empty: {
      title: 'Vous ne suivez rien pour l’instant',
      description: 'Le bouton « Suivre » d’un projet, d’un job ou d’un workflow l’ajoute ici.',
    },
  },
  reactions: {
    label: '{{reaction}} : {{count}}',
    names: {
      like: 'J’approuve',
      celebrate: 'Bravo',
      insightful: 'Instructif',
      eyes: 'Je regarde',
    },
  },
  comments: {
    show: 'Commentaires',
    hide: 'Masquer les commentaires',
    label: 'Votre commentaire',
    empty: 'Pas encore de commentaire.',
    submit: 'Commenter',
    save: 'Enregistrer',
    saved: 'Commentaire modifié.',
    edit: 'Modifier mon commentaire',
    delete: 'Supprimer mon commentaire',
    deleted: 'Commentaire supprimé.',
    edited: '(modifié)',
  },
  follow: {
    follow: 'Suivre',
    following: 'Suivi',
    followed: 'Vous suivez « {{name}} » : son activité apparaît dans votre fil.',
    unfollowed: 'Vous ne suivez plus « {{name}} ».',
    unavailable: 'Fonction indisponible sur cette installation : le volet social est coupé.',
    followers_one: '{{count}} abonné',
    followers_other: '{{count}} abonnés',
  },
  errors: {
    forbidden: {
      follow: 'Vos droits ne permettent pas de suivre cette ressource.',
      react: 'Vos droits ne permettent pas de réagir à cette activité.',
      comment: 'Vos droits ne permettent pas de commenter cette activité.',
      editComment: 'Seul l’auteur d’un commentaire peut le modifier.',
      deleteComment: 'Seul l’auteur d’un commentaire peut le supprimer.',
    },
    notFound: {
      follow: 'Cette ressource n’existe plus, ou vous ne la suiviez pas.',
      react: 'Cette activité n’existe plus, ou cette réaction n’était pas la vôtre.',
      comment: 'Cette activité n’existe plus.',
      editComment: 'Ce commentaire n’existe plus.',
      deleteComment: 'Ce commentaire n’existe plus.',
    },
    unavailable: 'Fonction indisponible sur cette installation : le volet social est coupé.',
    failed: 'Le geste n’a pas abouti : {{detail}}',
  },
};

export const galleryFr = {
  entry: 'Partir d’un gabarit',
  title: 'Gabarits',
  subtitle:
    'Des pipelines prêts à remplir, livrés avec graal : choisissez-en un, renseignez ses paramètres, il s’ouvre dans l’éditeur.',
  searchPlaceholder: 'Rechercher un gabarit…',
  backToGraphs: 'Graphes',
  columns: {
    name: 'Gabarit',
    description: 'Ce qu’il fait',
    latest: 'Dernière version',
  },
  empty: {
    title: 'Aucun gabarit',
    description: 'Cette installation ne livre aucun gabarit low-code.',
  },
  form: {
    graphName: 'Nom du graphe créé',
    graphNameHelp: 'Minuscules, chiffres et tirets. Le gabarit s’ouvre comme un nouveau graphe, modifiable.',
    jsonPlaceholder: 'Valeur JSON',
    errors: {
      array: 'Un tableau JSON est attendu, par exemple [{"name": "date", "type": "str"}].',
      object: 'Un objet JSON est attendu, par exemple {"cle": "valeur"}.',
    },
  },
  detail: {
    version: 'Version',
    parameters_one: '{{count}} paramètre',
    parameters_other: '{{count}} paramètres',
    formTitle: 'Renseigner et ouvrir',
    submit: 'Ouvrir dans l’éditeur',
    footer: 'Le DAG rendu s’ouvre dans le canevas : rien ne s’exécute avant « Créer un job ».',
    graphDescription: 'Créé depuis le gabarit {{template}} {{version}}.',
    opened: 'Graphe « {{name}} » créé depuis le gabarit.',
    importFailed: 'Le DAG rendu ne s’ouvre pas dans l’éditeur (tâche {{task}}) : {{reason}}',
  },
  importProblems: {
    notADag: 'la réponse n’est pas un DAG.',
    unsupportedTask: 'tâche « {{detail}} » sans boîte dans l’éditeur.',
    badConnector: 'connecteur « {{detail}} » non pris en charge.',
    literalCredentials: 'clé « {{detail}} » écrite en clair au lieu d’une référence.',
    badParams: 'paramètres illisibles ({{detail}}).',
    unknownDependency: 'dépendance inconnue « {{detail}} ».',
  },
  verify: {
    title: 'Vérification',
    action: 'Vérifier',
    valid: 'Le moteur accepte ce DAG',
    validHelp: 'Schéma de colonnes de chaque tâche, et frameworks capables de l’exécuter.',
    frameworks: 'Frameworks compatibles',
    noFramework: 'Aucun framework ne sait exécuter ce DAG.',
    schemas: 'Colonnes par tâche',
    unavailable: 'Le moteur low-code n’est pas activé sur cette installation.',
    forbidden:
      'Vos droits ne permettent pas de faire juger un DAG par le moteur (droit low-code d’exécution).',
    refused: 'Le moteur refuse ce DAG',
    refusedHelp: 'Le détail ci-dessous vient du moteur : il nomme la tâche ou la colonne en cause.',
  },
};
